/**
 * Quote de Story do Instagram para respostas e menções
 * Exibe referência visual ao Story original dentro do balão de mensagem
 * Similar ao estilo de citação/reply do WhatsApp
 * 
 * @version 1.0.0
 * @date 2026-01-18
 */

import { useState } from 'react';
import { ExternalLink, Film } from 'lucide-react';
import { cn } from '@/lib/utils';

// Interface de props do componente
export interface StoryQuoteBubbleProps {
  /** URL da thumbnail do story */
  storyUrl?: string | null;
  /** Link para abrir no Instagram (opcional) */
  storyPermalink?: string | null;
  /** Direção da mensagem (para estilização) */
  direction: 'inbound' | 'outbound';
  /** Tipo de mensagem: story_reply ou story_mention */
  messageType: 'story_reply' | 'story_mention';
  /** Callback ao clicar em link externo */
  onExternalClick?: () => void;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Componente que exibe a referência ao Story dentro do balão da mensagem
 * Estilo visual similar ao quote/citação de mensagens, com gradiente do Instagram Stories
 */
export function StoryQuoteBubble({
  storyUrl,
  storyPermalink,
  direction,
  messageType,
  onExternalClick,
  className,
}: StoryQuoteBubbleProps) {
  const [imageError, setImageError] = useState(false);
  const isInbound = direction === 'inbound';
  
  // Label baseado no tipo de mensagem
  const label = messageType === 'story_mention' 
    ? 'Menção no Story' 
    : 'Resposta ao Story';

  // Handler para abrir link externo
  const handleClick = () => {
    if (storyPermalink) {
      window.open(storyPermalink, '_blank', 'noopener,noreferrer');
    }
    onExternalClick?.();
  };

  return (
    <div
      onClick={storyPermalink ? handleClick : undefined}
      className={cn(
        'flex items-center gap-2 p-1.5 mb-1.5 rounded-lg -mx-1',
        'border-l-3 transition-colors',
        storyPermalink && 'cursor-pointer',
        // Gradiente roxo/rosa característico do Instagram Stories
        isInbound
          ? 'bg-gradient-to-r from-purple-50/50 to-pink-50/50 dark:from-purple-950/20 dark:to-pink-950/20 border-l-purple-500'
          : 'bg-primary-foreground/10 border-l-purple-300',
        storyPermalink && (isInbound 
          ? 'hover:from-purple-100/50 hover:to-pink-100/50' 
          : 'hover:bg-primary-foreground/15'),
        className
      )}
      role={storyPermalink ? 'button' : undefined}
      tabIndex={storyPermalink ? 0 : undefined}
      onKeyDown={(e) => {
        if (storyPermalink && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Thumbnail do story */}
      <div className="relative h-10 w-10 rounded-lg overflow-hidden bg-muted flex-shrink-0">
        {storyUrl && !imageError ? (
          <img
            src={storyUrl}
            alt="Story do Instagram"
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className={cn(
            'h-full w-full flex items-center justify-center',
            'bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30'
          )}>
            <Film className="h-5 w-5 text-purple-500" />
          </div>
        )}
      </div>

      {/* Informações do story - cores fixas para melhor contraste */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span 
            className={cn(
              'text-xs font-medium',
              isInbound && 'text-purple-700 dark:text-purple-400'
            )}
            style={!isInbound ? { color: '#7c3aed' } : undefined}
          >
            {label}
          </span>
          {storyPermalink && (
            <ExternalLink 
              className={cn(
                'h-3 w-3 flex-shrink-0',
                isInbound && 'text-purple-500 dark:text-purple-400'
              )} 
              style={!isInbound ? { color: '#7c3aed' } : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
}
